import CopyAllIcon from "@mui/icons-material/CopyAll";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import CreateItemButton from "../../components/CreateItemButton.js";
import ErrorDialog from "../../components/dialogs/ErrorDialog.js";
import LoadingBackdrop from "../../components/LoadingBackdrop.js";
import LogsList from "../../components/page-item/LogsList.js";
import PageListHeader from "../../components/page-list/PageListHeader.js";
import { getRun, postRunCopy, postRun } from "../../lib/apiConfig.js";
import {
  PAGE_CONTAINER_SX,
  PAGE_TITLE_SX,
  ROUTES,
} from "../../lib/constants.js";
import { formatDate } from "../../lib/formatDate.js";
import { useFetch } from "../../lib/hooks/useFetch.js";
import CopyRunDialog from "./components/dialogs/CopyRunDialog.js";

export default function RunPage({ locales }) {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [runId, setRunId] = useState(null);
  const [runCopyId, setRunCopyId] = useState(null);
  const [run, setRun] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [isAscending, setIsAscending] = useState(false);
  const [showCopyRunDialog, setShowCopyRunDialog] = useState(false);
  const [runName, setRunName] = useState("");
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const setError = (error) => {
    setErrorMessage(error.message);
    setShowError(true);
  };

  const { isFetching: isFetchingRun } = useFetch({
    endpoint: getRun({ id: runId }),
    mustFetch: runId && !run,
    onSuccess: setRun,
    onFail: setError,
  });

  const { fetchData: createRun, isFetching: isCreatingRun } = useFetch({
    endpoint: postRun({ runId }),
    onSuccess: (response) => {
      const runCopyId = response.id;

      setRunCopyId(runCopyId);
      copyRun({ fromRunId: runId, toRunId: runCopyId });
    },
    onFail: setError,
  });

  const { fetchData: copyRun, isFetching: isCopyingRun } = useFetch({
    endpoint: postRunCopy({ id: runId }),
    onFail: setError,
  });

  useEffect(() => {
    setRunId(state.id);
  }, [state]);

  useEffect(() => {
    if (runCopyId && !isCopyingRun) {
      navigate(`/${ROUTES.RUNS.DETAIL.replace(":id", runCopyId)}`, {
        replace: true,
        state: { key: Date.now(), id: runCopyId },
      });
    }
  }, [isCopyingRun, navigate, runCopyId]);

  const filteredMessages = useMemo(() => {
    if (!run?.logs) return [];

    const filtered = run.logs.filter((log) =>
      log.message.toLowerCase().includes(searchValue.toLowerCase())
    );

    return filtered.sort(
      (a, b) =>
        (new Date(a.timestamp) - new Date(b.timestamp)) * (isAscending ? 1 : -1)
    );
  }, [isAscending, run, searchValue]);

  const handleInput = ({ target }) => {
    setSearchValue(target.value);
  };

  const handleClearInput = () => {
    setSearchValue("");
  };

  const handleSort = () => {
    setIsAscending(!isAscending);
  };

  const handleCopyRunClick = () => {
    setShowCopyRunDialog(false);
    createRun({ name: runName });
  };

  return !run || isFetchingRun || isCreatingRun || isCopyingRun ? (
    <LoadingBackdrop />
  ) : (
    <Container disableGutters sx={PAGE_CONTAINER_SX}>
      <Box
        id="run-header"
        display="flex"
        alignItems="start"
        justifyContent="space-between"
      >
        <Typography component="h1" sx={PAGE_TITLE_SX}>
          {run.name}
        </Typography>
        <CreateItemButton
          icon={<CopyAllIcon />}
          onClick={() => setShowCopyRunDialog(true)}
          text={locales["run.copy"]}
        />
      </Box>
      <PageListHeader
        handleClearInput={handleClearInput}
        handleInput={handleInput}
        handleSort={handleSort}
        locales={locales}
        isAscending={isAscending}
        pageName="run-log"
        searchBoxLabel={locales["logs.search"]}
        searchValue={searchValue}
      />
      {filteredMessages.length ? (
        <LogsList
          id="run-log"
          items={filteredMessages}
          renderPrimary={(log) => log.message}
          renderSecondary={(log) => formatDate(log.timestamp)}
        />
      ) : (
        <Typography component="p">{locales["search.empty"]}</Typography>
      )}
      <CopyRunDialog
        locales={locales}
        onInput={({ target }) => {
          setRunName(target.value?.replaceAll(" ", ""));
        }}
        onClose={() => {
          setShowCopyRunDialog(false);
          setRunName("");
        }}
        onConfirm={handleCopyRunClick}
        name={runName}
        show={showCopyRunDialog}
      />
      <ErrorDialog
        locales={locales}
        message={errorMessage}
        onClose={() => {
          setShowError(false);
          setErrorMessage(null);
        }}
        show={showError}
      />
    </Container>
  );
}
